import { useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StorePerformanceInterval, createStorePerformanceRequest } from '../../../../../constants';
import { useEventContext } from '../../../../../global-state/eventContext';
import { useThemeContext } from '../../../../../global-state/themeContext';
import { SalesNetPerReceiptPerformanceItem, useStorePerformance, useTransaction } from '../../../../../hooks';
import { useCurrencyTools } from '../../../../../hooks/useCurrencyTools';
import { Icons } from '../../../../../stories/atoms';
import { ContainerV2, RowFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { muiTheme } from '../../../../../theme';
import { homeTranslations } from '../../../../../translations';
import { formatAmountWithSeparator, isFailureResponse } from '../../../../../utils';
import { localeMap } from '../../../../../utils/language';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import WidgetChart from '../../../common/widgetChart/WidgetChart';
import {
  CurrentPeriodCard,
  getWidgetTimeFrameLabel,
  TrendCard,
} from '../../../common/widgetSummaryCards/WidgetSummaryCards';

export type AveragePurchaseWidgetProps = {
  interval: StorePerformanceInterval;
  currentDate: Date;
  isToday: boolean;
};

export const AveragePurchaseWidget: FC<AveragePurchaseWidgetProps> = observer(
  ({ interval, currentDate, isToday }) => {
    const { t, i18n } = useTranslation();
    const { selectedCurrency, getConvertedAmount, getFormattedCurrency } = useCurrencyTools(interval, currentDate);

    const { fetchSalesNetPerReceiptData } = useTransaction();
    const { transactionEventsCount } = useEventContext();

    const { averagePurchase: averagePurchaseTranslation } = homeTranslations;
    const { mode } = useThemeContext();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

    const changeNote = isToday
      ? ''
      : t(
          homeTranslations.common.changeVsCurrentNote.key,
          homeTranslations.common.changeVsCurrentNote.defaultValue,
        );

    const [today, setToday] = useState(0);
    const [lastWeek, setLastWeek] = useState(0);
    const [lastYear, setLastYear] = useState(0);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

    const {
      performanceItems,
      performanceItemsLoading,
      performanceItemsError,

      filteredPerformanceItems,

      forecastPerformanceItems,
      triggerGetStorePerformance,
    } = useStorePerformance<SalesNetPerReceiptPerformanceItem>();

    const itemValueResolver = (item: SalesNetPerReceiptPerformanceItem) => {
      return getConvertedAmount(item.amount);
    };

    const fetchSalesNetPerReceipt = useCallback(async () => {
      const request = createStorePerformanceRequest({
        interval,
        currentDate,
      });

      const response = await fetchSalesNetPerReceiptData(request);

      if (isFailureResponse(response)) {
        throw new Error();
      }

      setToday(response.data.today);
      setLastWeek(response.data.lastWeek);
      setLastYear(response.data.lastYear);
      setLastUpdated(response.data.lastUpdated);

      return response.data.netSalePerReceipt;
    }, [interval, currentDate]);

    const triggerGetStorePerformanceRef = useRef(triggerGetStorePerformance);
    useEffect(() => {
      triggerGetStorePerformanceRef.current = triggerGetStorePerformance;
    }, [triggerGetStorePerformance]);

    const fetchWidgetData = useCallback(() => {
      triggerGetStorePerformanceRef.current({
        interval: interval,
        currentDate: currentDate,
        isToday: isToday,
        getDataAction: fetchSalesNetPerReceipt,
        getHasValue: (item) => item.amount !== 0,
      });
    }, [interval, currentDate, isToday, fetchSalesNetPerReceipt]);

    const weekLabel = t(
      homeTranslations.datePicker.weekLabel.key,
      homeTranslations.datePicker.weekLabel.defaultValue,
    );
    const dateLocale = localeMap[i18n.language] ?? localeMap['en-US'];
    const timeFrame = useMemo(
      () => getWidgetTimeFrameLabel(interval, currentDate, weekLabel, dateLocale),
      [interval, currentDate, weekLabel, dateLocale],
    );

    useEffect(() => {
      fetchWidgetData();
    }, [fetchWidgetData, transactionEventsCount]);

    const isNoData = !filteredPerformanceItems.some((item) => item !== null);
    const backgroundColor = mode === 'dark' ? 'backgroundSelectedHover' : 'white';
    const valueFormatter = (value: number) => getFormattedCurrency(value, { showCurrencyCode: false });

    return (
      <WidgetCardContent
        headerIcon={Icons.SALESEFFICIENCY}
        headerText={t(averagePurchaseTranslation.title.key, averagePurchaseTranslation.title.defaultValue)}
        subHeaderText={t(
          averagePurchaseTranslation.subTitle.key,
          averagePurchaseTranslation.subTitle.defaultValue,
        )}
        lastUpdatedDate={lastUpdated}
        isLoading={performanceItemsLoading}
        isError={performanceItemsError}
        errorText={t(averagePurchaseTranslation.error.key, averagePurchaseTranslation.error.defaultValue)}
        errorRetryHandler={fetchWidgetData}
        isNoData={isNoData}
        toolTipText={t(averagePurchaseTranslation.toolTip.key, averagePurchaseTranslation.toolTip.defaultValue)}
        content={
          <ContainerV2
            data-testid="salesnet-per-receipt-wrapper"
            height="100%"
            display="flex"
            flexDirection="column"
            minHeight={[0]}
          >
            <RowFlexContainer alignItems="stretch" gap={[2]} padding={[4, 5, 0, 5]}>
              <CurrentPeriodCard
                backgroundColor={backgroundColor}
                tooltipText={t(
                  averagePurchaseTranslation.currentPeriodTooltip.key,
                  averagePurchaseTranslation.currentPeriodTooltip.defaultValue,
                )}
                tooltipTestId="average-purchase-current-period-info-icon"
                value={valueFormatter(today)}
                timeFrame={timeFrame}
              />

              <TrendCard
                originalAmount={today}
                previousAmount={lastWeek}
                label={t(homeTranslations.common.lastWeek.key, homeTranslations.common.lastWeek.defaultValue)}
                backgroundColor={backgroundColor}
                hideTrendText={isToday}
                tooltipText={t(
                  averagePurchaseTranslation.lastWeekTooltip.key,
                  averagePurchaseTranslation.lastWeekTooltip.defaultValue,
                  { changeNote },
                )}
                tooltipTestId="average-purchase-last-week-info-icon"
                valueFormatter={valueFormatter}
              />
              <TrendCard
                originalAmount={today}
                previousAmount={lastYear}
                label={t(homeTranslations.common.lastYear.key, homeTranslations.common.lastYear.defaultValue)}
                backgroundColor={backgroundColor}
                hideTrendText={isToday}
                tooltipText={t(
                  averagePurchaseTranslation.lastYearTooltip.key,
                  averagePurchaseTranslation.lastYearTooltip.defaultValue,
                  { changeNote },
                )}
                tooltipTestId="average-purchase-last-year-info-icon"
                valueFormatter={valueFormatter}
              />
            </RowFlexContainer>

            <ContainerV2 data-testid="chart" style={{ flex: 1, minHeight: 0 }}>
              <WidgetChart
                type="bar"
                performanceItemsChartName={t(
                  averagePurchaseTranslation.toolTip.key,
                  averagePurchaseTranslation.toolTip.defaultValue,
                )}
                performanceItems={performanceItems}
                filteredPerformanceItems={filteredPerformanceItems}
                forecastPerformanceItems={forecastPerformanceItems}
                itemValueResolver={itemValueResolver}
                interval={interval}
                showXAxis={true}
                showYAxis={false}
                tooltipFormatter={(value) => formatAmountWithSeparator(value, selectedCurrency)}
                dataLabel={{
                  enabled: true,
                  enabledOnSeries: [0, 2],
                  offsetY: -28,
                  style: {
                    fontSize: isMobile ? '8px' : '12px',
                    fontWeight: '100',
                    colors: [mode === 'dark' ? muiTheme.color.white : muiTheme.color.primary],
                  },
                  formatter: function (value) {
                    if (!value) return '';
                    return formatAmountWithSeparator(Number(value));
                  },
                }}
              />
            </ContainerV2>
          </ContainerV2>
        }
      />
    );
  },
);
